/**
 * ConceptoPicker — combobox de un solo campo "Concepto" (spec §5.1)
 *
 * Reemplaza los dos autocompletes (categoría + concepto) por uno solo:
 * - Opciones agrupadas por categoría (header de grupo), los más usados arriba.
 * - Buscador unificado sobre 3 fuentes + fuzzy (P3a Task 4, ver
 *   helpers/buscadorConceptos): por nombre (containment bidireccional con
 *   Detalle pre-llenado), por categoría (sección completa), por detalle
 *   histórico (hint "detalle usado antes") y fuzzy "¿Quisiste decir?".
 * - "+ Crear «query»" siempre visible al final cuando hay búsqueda, con
 *   mini-flow inline: elegir categoría (o crear una) → crea el concepto de la
 *   empresa heredando unidad/gestión de la categoría. Si la categoría es
 *   alimentacion_engorde: unidad kg forzada, PB/EM con auto-fill del INTA
 *   (§4.2) y unidad de carga opcional con kg por unidad (§3.7).
 */
import React, { useState, useEffect, useRef, useMemo, useId } from 'react';
import { getRepositories } from '../../services/storage';
import {
  Concepto,
  Categoria,
  TipoCategoria,
  GestionConcepto,
} from '../../types/administracion';
import { normalizarTexto, UNIDADES_MEDIDA } from '../../helpers/conceptosHelper';
import { buscarConceptos, MatchDeBusqueda } from '../../helpers/buscadorConceptos';

export interface ConceptoPickerProps {
  value: Concepto | null;
  /**
   * Se llama al elegir/crear un concepto. `detallePrefill` llega del brazo de
   * containment bidireccional ("flete alfalfa" → Flete + "alfalfa") para que
   * el form pre-llene su campo Detalle.
   */
  onChange: (concepto: Concepto | null, detallePrefill?: string) => void;
  /** Filtra por tipo de formulario (spec §5.1): gasto → alimentacion_engorde/otros_costos_engorde/fijo/otro; ingreso → ingreso */
  tiposPermitidos?: TipoCategoria[];
  /** compra/carga de stock → solo gestion=inventario */
  soloInventario?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** id del input (para asociar un <label htmlFor> del formulario host) */
  id?: string;
}

const UNIDADES = UNIDADES_MEDIDA as readonly string[];

/** Validación runtime de unidad (carry del Task 4: el listado cerrado vive en el helper) */
const esUnidadValida = (u: string): boolean => UNIDADES.includes(u);

/** Unidades de carga coherentes con conversión a kg (spec §3.7 + §4.2) */
const UNIDADES_CARGA_KG: readonly string[] = ['kg', 'unidades', 'rollos'];

const TIPOS_CATEGORIA: { value: TipoCategoria; label: string }[] = [
  { value: 'alimentacion_engorde', label: 'Alimentación (engorde)' },
  { value: 'otros_costos_engorde', label: 'Otros costos de engorde' },
  { value: 'fijo', label: 'Costo fijo' },
  { value: 'ingreso', label: 'Ingreso' },
  { value: 'otro', label: 'Otro' },
];

/** Auto-fill de PB/EM y kg por unidad para conceptos de alimentación (seeds §4.2, clave normalizada) */
const NUTRICION_SEED: Record<string, { pb: number; em: number; unidad_carga?: string; kg_por_unidad?: number }> = {
  'grano de maiz': { pb: 9.5, em: 3.18 },
  'silo de maiz': { pb: 8.0, em: 2.6 },
  'silo de sorgo': { pb: 7.0, em: 2.3 },
  'pastura': { pb: 14.0, em: 2.5 },
  'afrecho de soja': { pb: 48.0, em: 3.3 },
  'heno de alfalfa': { pb: 18.0, em: 2.4, unidad_carga: 'rollos', kg_por_unidad: 250 },
  'grano de sorgo': { pb: 11.0, em: 3.15 },
  'sal mineral': { pb: 0, em: 0, unidad_carga: 'unidades', kg_por_unidad: 25 },
  'megafardo': { pb: 10.0, em: 2.1, unidad_carga: 'unidades', kg_por_unidad: 450 },
  'megafardo de alfalfa': { pb: 18.0, em: 2.4, unidad_carga: 'unidades', kg_por_unidad: 450 },
  'balanceado hiper precoz': { pb: 20.0, em: 3.0, unidad_carga: 'unidades', kg_por_unidad: 25 },
  'balanceado precoz': { pb: 18.0, em: 2.96, unidad_carga: 'unidades', kg_por_unidad: 25 },
  'balanceado recría': { pb: 15.0, em: 2.75, unidad_carga: 'unidades', kg_por_unidad: 25 },
  'balanceado engorde': { pb: 13.0, em: 2.8, unidad_carga: 'unidades', kg_por_unidad: 25 },
};

const inputClassName =
  'w-full rounded-md border border-hairline bg-white px-3 py-2 text-ink placeholder:text-sage/70 text-base focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/20';

export const ConceptoPicker: React.FC<ConceptoPickerProps> = ({
  value,
  onChange,
  tiposPermitidos,
  soloInventario = false,
  placeholder = 'Buscar o crear concepto…',
  disabled = false,
  id,
}) => {
  // Catálogo (se carga al montar)
  const [conceptos, setConceptos] = useState<Concepto[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [frecuencias, setFrecuencias] = useState<Record<string, number>>({});
  const [detalles, setDetalles] = useState<Record<string, string[]>>({});
  const [empresaId, setEmpresaId] = useState<string | null>(null);

  // Combobox
  const [query, setQuery] = useState<string | null>(null); // null = muestra el nombre del value
  const [open, setOpen] = useState(false);
  const [creando, setCreando] = useState(false);

  // Mini-flow de creación
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [categoriaElegidaId, setCategoriaElegidaId] = useState('');
  const [nuevaCat, setNuevaCat] = useState(false);
  const [nuevaCatNombre, setNuevaCatNombre] = useState('');
  const [nuevaCatTipo, setNuevaCatTipo] = useState<TipoCategoria>(tiposPermitidos?.[0] ?? 'otro');
  const [nuevaCatUnidad, setNuevaCatUnidad] = useState('unidades');
  const [unidad, setUnidad] = useState('');
  const [gestion, setGestion] = useState<GestionConcepto>('gasto_directo');
  const [pb, setPb] = useState('');
  const [em, setEm] = useState('');
  const [unidadCarga, setUnidadCarga] = useState('');
  const [kgPorUnidad, setKgPorUnidad] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { concepto, categoria, profile } = getRepositories();
        const perfil = await profile.getCurrentProfile();
        const empresa = perfil?.empresa_activa_id ?? '';
        const [concepts, freqs, cats, dets] = await Promise.all([
          concepto.listarVisibles(empresa),
          concepto.getFrecuencias(empresa),
          categoria.listarParaEmpresa(empresa),
          // Índice de detalles históricos para el buscador (degrada si el repo
          // es viejo y no lo implementa)
          typeof concepto.getDetallesHistoricos === 'function'
            ? concepto.getDetallesHistoricos(empresa)
            : Promise.resolve({} as Record<string, string[]>),
        ]);
        if (!cancelled) {
          setConceptos(concepts);
          setFrecuencias(freqs);
          setCategorias(cats);
          setDetalles(dets ?? {});
          setEmpresaId(empresa || null);
        }
      } catch (e) {
        console.error('ConceptoPicker: error cargando taxonomía', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Cerrar al hacer click fuera
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
        setCreando(false);
        setNuevaCat(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const categoriasById = useMemo(() => new Map(categorias.map(c => [c.id, c])), [categorias]);

  const categoriasDisponibles = useMemo(() => {
    if (!tiposPermitidos) return categorias;
    return categorias.filter(c => tiposPermitidos.includes(c.tipo));
  }, [categorias, tiposPermitidos]);

  // Conceptos que pasan el filtro del formulario (tipo + gestión)
  const conceptosBase = useMemo(
    () =>
      conceptos.filter(c => {
        const cat = categoriasById.get(c.categoria_id);
        if (!cat) return false;
        if (tiposPermitidos && !tiposPermitidos.includes(cat.tipo)) return false;
        if (soloInventario && c.gestion !== 'inventario') return false;
        return true;
      }),
    [conceptos, categoriasById, tiposPermitidos, soloInventario]
  );

  // Buscador unificado (P3a Task 4): 3 fuentes + fuzzy, texto normalizado (§5.5).
  // Sin query devuelve todos; con query matchea por nombre (bidireccional),
  // categoría, detalle histórico y fuzzy (ver helpers/buscadorConceptos).
  const busqueda = useMemo(
    () => buscarConceptos(query ?? '', conceptosBase, categorias, detalles),
    [conceptosBase, categorias, detalles, query]
  );

  // Orden por frecuencia (§3.9) + nombre; las sugerencias fuzzy van aparte al final
  const porFrecuencia = (a: MatchDeBusqueda, b: MatchDeBusqueda) =>
    (frecuencias[b.concepto.id] ?? 0) - (frecuencias[a.concepto.id] ?? 0) ||
    a.concepto.nombre.localeCompare(b.concepto.nombre, 'es');

  const matches = useMemo(
    () => busqueda.filter(m => !m.fuzzy).sort(porFrecuencia),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busqueda, frecuencias]
  );
  const sugerenciasFuzzy = useMemo(() => busqueda.filter(m => m.fuzzy), [busqueda]);

  // "Más usados" fue retirado a pedido del dueño: el listado del picker es
  // SÓLO el catálogo curado agrupado por categoría (sin la lista vieja de
  // frecuencias de uso / cache). El orden interno sigue siendo por uso (§3.9).

  // Agrupado por categoría (orden de categorias = nombre, de listarParaEmpresa)
  const grupos = useMemo(() => {
    const porCategoria = new Map<string, MatchDeBusqueda[]>();
    for (const m of matches) {
      const cat = categoriasById.get(m.concepto.categoria_id);
      if (!cat) continue;
      const arr = porCategoria.get(cat.id) ?? [];
      arr.push(m);
      porCategoria.set(cat.id, arr);
    }
    return categoriasDisponibles
      .filter(cat => porCategoria.has(cat.id))
      .map(cat => ({ categoria: cat, matches: porCategoria.get(cat.id)! }));
  }, [matches, categoriasById, categoriasDisponibles]);

  const categoriaElegida = categoriaElegidaId ? categoriasById.get(categoriaElegidaId) : undefined;
  const esAlimentacion = categoriaElegida?.tipo === 'alimentacion_engorde';

  // Al elegir categoría: heredar unidad/gestión y auto-fill INTA si matchea seed
  useEffect(() => {
    if (!categoriaElegida) return;
    if (categoriaElegida.tipo === 'alimentacion_engorde') {
      setUnidad('kg'); // obligatoria (spec §8)
      setGestion('inventario');
      const seed = NUTRICION_SEED[normalizarTexto(nombreNuevo)];
      if (seed) {
        setPb(String(seed.pb));
        setEm(String(seed.em));
        setUnidadCarga(seed.unidad_carga ?? '');
        setKgPorUnidad(seed.kg_por_unidad != null ? String(seed.kg_por_unidad) : '');
      } else {
        setPb('');
        setEm('');
        setUnidadCarga('');
        setKgPorUnidad('');
      }
    } else {
      const ud = categoriaElegida.unidad_default ?? '';
      setUnidad(esUnidadValida(ud) ? ud : 'unidades');
      setGestion(soloInventario ? 'inventario' : 'gasto_directo');
    }
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriaElegidaId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    if (creando) cerrarCrear(); // tipear de nuevo → vuelve a la búsqueda
    // Stale-selection: el concepto seleccionado sólo es válido mientras el
    // texto del input sigue siendo su nombre (comparación normalizada). Si el
    // usuario edita el texto a otra cosa, la selección se limpia para que el
    // form no submita un concepto_id que ya no representa lo que se ve.
    if (value && normalizarTexto(v) !== normalizarTexto(value.nombre)) {
      onChange(null);
    }
  };

  const handleSelect = (c: Concepto, detallePrefill?: string) => {
    onChange(c, detallePrefill);
    setQuery(null);
    setOpen(false);
    setCreando(false);
  };

  const abrirCrear = () => {
    setNombreNuevo((query ?? '').trim());
    setCategoriaElegidaId('');
    setNuevaCat(false);
    setError(null);
    setCreando(true);
  };

  const cerrarCrear = () => {
    setCreando(false);
    setNuevaCat(false);
    setError(null);
  };

  const parseNumero = (s: string): number | null => {
    const n = parseFloat(s.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };

  const handleGuardar = async () => {
    if (!empresaId) {
      // Sin empresa activa el alta quedaría empresa_id NULL = global (solo seed)
      // y la RLS la rechazaría en sync dejando un pending en retry perpetuo.
      setError('No se pudo determinar la empresa activa. Probá de nuevo.');
      return;
    }
    const cat = categoriaElegida;
    if (!cat) {
      setError('Elegí una categoría');
      return;
    }
    const nombre = nombreNuevo.trim();
    if (!nombre) {
      setError('El nombre no puede estar vacío');
      return;
    }

    const unidadFinal = esAlimentacion ? 'kg' : esUnidadValida(unidad) ? unidad : 'unidades';
    const gestionFinal: GestionConcepto =
      esAlimentacion || soloInventario ? 'inventario' : gestion;

    let nutricion: Concepto['nutricion'];
    let carga: Pick<Concepto, 'unidad_carga' | 'kg_por_unidad'> = {};
    if (esAlimentacion) {
      const pbNum = parseNumero(pb);
      const emNum = parseNumero(em);
      if (pb !== '' && em !== '' && pbNum !== null && emNum !== null) {
        nutricion = { pb: pbNum, em: emNum };
      }
      if (unidadCarga && unidadCarga !== 'kg') {
        const kgNum = parseNumero(kgPorUnidad);
        if (kgNum !== null && kgNum > 0) {
          carga = { unidad_carga: unidadCarga, kg_por_unidad: kgNum };
        }
      }
    }

    setGuardando(true);
    setError(null);
    try {
      const { concepto } = getRepositories();
      const data: Omit<Concepto, 'id'> = {
        categoria_id: cat.id,
        empresa_id: empresaId,
        nombre,
        unidad: unidadFinal,
        gestion: gestionFinal,
        activo: true,
        ...(nutricion ? { nutricion } : {}),
        ...carga,
      };
      const id = await concepto.add(data);
      const nuevo: Concepto = { ...data, id };
      setConceptos(prev => [...prev, nuevo]);
      onChange(nuevo);
      setQuery(null);
      setOpen(false);
      cerrarCrear();
    } catch (e) {
      console.error('ConceptoPicker: error creando concepto', e);
      setError('No se pudo crear el concepto');
    } finally {
      setGuardando(false);
    }
  };

  const handleGuardarCategoria = async () => {
    if (!empresaId) {
      setError('No se pudo determinar la empresa activa. Probá de nuevo.');
      return;
    }
    const nombre = nuevaCatNombre.trim();
    if (!nombre) {
      setError('Ponele nombre a la categoría');
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      const { categoria } = getRepositories();
      const data: Omit<Categoria, 'id'> = {
        empresa_id: empresaId,
        nombre,
        tipo: nuevaCatTipo,
        unidad_default: esUnidadValida(nuevaCatUnidad) ? nuevaCatUnidad : 'unidades',
        activo: true,
      };
      const id = await categoria.add(data);
      const nueva = { ...data, id };
      setCategorias(prev =>
        [...prev, nueva].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
      );
      setNuevaCat(false);
      setNuevaCatNombre('');
      setCategoriaElegidaId(id); // queda seleccionada para el concepto nuevo
    } catch (e) {
      console.error('ConceptoPicker: error creando categoría', e);
      setError('No se pudo crear la categoría');
    } finally {
      setGuardando(false);
    }
  };

  const inputValor = query ?? value?.nombre ?? '';
  const textoBusqueda = (query ?? '').trim();
  const sinResultados = matches.length === 0 && sugerenciasFuzzy.length === 0;

  const renderOpcion = (m: Pick<MatchDeBusqueda, 'concepto' | 'etiqueta' | 'detallePrefill' | 'fuzzy'>) => (
    <li
      key={m.concepto.id}
      role="option"
      aria-selected={value?.id === m.concepto.id}
      className={`px-4 py-2 cursor-pointer text-ink border-b border-hairline/40 hover:bg-forest/10 flex items-center justify-between gap-2 ${
        value?.id === m.concepto.id ? 'bg-forest/10' : ''
      }`}
      onClick={() => handleSelect(m.concepto, m.detallePrefill)}
    >
      <span className="truncate">{m.concepto.nombre}</span>
      <span className="flex items-center gap-2 whitespace-nowrap">
        {m.etiqueta && <span className="text-xs text-sage">{m.etiqueta}</span>}
        {m.fuzzy && (
          <span className="text-xs rounded bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5">
            ¿Quisiste decir?
          </span>
        )}
      </span>
    </li>
  );

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <input
          type="text"
          role="combobox"
          id={id}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls={open ? listId : undefined}
          autoComplete="off"
          className={`${inputClassName} ${disabled ? 'bg-hairline/40 cursor-not-allowed' : ''} pr-9`}
          value={inputValor}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              setOpen(false);
              cerrarCrear();
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
        />
        <i className="bi bi-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-sage pointer-events-none text-sm" />
      </div>

      {open && creando ? (
        /* ==================== Mini-flow crear concepto (§7) ==================== */
        <div
          className="absolute z-10 w-full mt-1 rounded-md border border-hairline bg-paper shadow-overlay max-h-96 overflow-auto p-3 space-y-3"
          onKeyDown={e => {
            if (e.key === 'Escape') cerrarCrear();
          }}
        >
          <p className="text-sm text-ink">
            Nuevo concepto: <span className="font-semibold">“{nombreNuevo}”</span>
          </p>

          {!nuevaCat ? (
            <div>
              <label htmlFor={`${listId}-cat`} className="block text-sm font-medium text-ink mb-1">
                Categoría
              </label>
              <select
                id={`${listId}-cat`}
                value={categoriaElegidaId}
                onChange={e => setCategoriaElegidaId(e.target.value)}
                className={inputClassName}
              >
                <option value="">Elegir categoría…</option>
                {categoriasDisponibles.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.nombre}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setNuevaCat(true)}
                className="mt-1 text-sm text-forest hover:underline"
              >
                + Nueva categoría
              </button>
            </div>
          ) : (
            <div className="rounded-md border border-hairline bg-white p-2 space-y-2">
              <p className="text-sm font-medium text-ink">Nueva categoría</p>
              <input
                className={inputClassName}
                placeholder="Nombre de la categoría"
                value={nuevaCatNombre}
                onChange={e => setNuevaCatNombre(e.target.value)}
              />
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-sm text-ink">
                  Tipo
                  <select
                    className={`mt-1 ${inputClassName}`}
                    value={nuevaCatTipo}
                    onChange={e => setNuevaCatTipo(e.target.value as TipoCategoria)}
                  >
                    {(tiposPermitidos
                      ? TIPOS_CATEGORIA.filter(t => tiposPermitidos.includes(t.value))
                      : TIPOS_CATEGORIA
                    ).map(t => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm text-ink">
                  Unidad default
                  <select
                    className={`mt-1 ${inputClassName}`}
                    value={nuevaCatUnidad}
                    onChange={e => setNuevaCatUnidad(e.target.value)}
                  >
                    {UNIDADES.map(u => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleGuardarCategoria}
                  disabled={guardando}
                  className="rounded-md bg-forest px-3 py-1.5 text-sm text-white hover:bg-forest/90 disabled:opacity-50"
                >
                  Crear categoría
                </button>
                <button
                  type="button"
                  onClick={() => setNuevaCat(false)}
                  className="rounded-md border border-hairline px-3 py-1.5 text-sm text-ink hover:bg-forest/10"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {categoriaElegida && !nuevaCat && (
            <>
              {esAlimentacion ? (
                <div className="space-y-2">
                  <p className="text-xs text-sage">
                    Unidad base <span className="font-medium text-ink">kg</span> (obligatoria para
                    alimentación)
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block text-sm text-ink">
                      PB (%)
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        className={`mt-1 ${inputClassName}`}
                        value={pb}
                        onChange={e => setPb(e.target.value)}
                        placeholder="Proteína bruta"
                      />
                    </label>
                    <label className="block text-sm text-ink">
                      EM (Mcal/kg)
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={`mt-1 ${inputClassName}`}
                        value={em}
                        onChange={e => setEm(e.target.value)}
                        placeholder="Energía metabólica"
                      />
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block text-sm text-ink">
                      Unidad de carga (opcional)
                      <select
                        className={`mt-1 ${inputClassName}`}
                        value={unidadCarga}
                        onChange={e => setUnidadCarga(e.target.value)}
                      >
                        <option value="">Directo en kg</option>
                        {UNIDADES_CARGA_KG.map(u => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </select>
                    </label>
                    {unidadCarga && unidadCarga !== 'kg' ? (
                      <label className="block text-sm text-ink">
                        kg por {unidadCarga}
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          className={`mt-1 ${inputClassName}`}
                          value={kgPorUnidad}
                          onChange={e => setKgPorUnidad(e.target.value)}
                          placeholder="Ej: 450"
                        />
                      </label>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-sm text-ink">
                    Unidad
                    <select
                      className={`mt-1 ${inputClassName}`}
                      value={unidad}
                      onChange={e => setUnidad(e.target.value)}
                    >
                      {UNIDADES.map(u => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </select>
                  </label>
                  {soloInventario ? (
                    <p className="text-xs text-sage self-end">Gestión: inventario (compra de stock)</p>
                  ) : (
                    <label className="block text-sm text-ink">
                      Gestión
                      <select
                        className={`mt-1 ${inputClassName}`}
                        value={gestion}
                        onChange={e => setGestion(e.target.value as GestionConcepto)}
                      >
                        <option value="gasto_directo">Gasto directo</option>
                        <option value="inventario">Inventario</option>
                      </select>
                    </label>
                  )}
                </div>
              )}
            </>
          )}

          {error && <p className="text-sm text-danger">{error}</p>}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={cerrarCrear}
              className="rounded-md border border-hairline px-3 py-1.5 text-sm text-ink hover:bg-forest/10"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleGuardar}
              disabled={guardando || !categoriaElegidaId}
              className="rounded-md bg-forest px-3 py-1.5 text-sm text-white hover:bg-forest/90 disabled:opacity-50"
            >
              Crear concepto
            </button>
          </div>
        </div>
      ) : open ? (
        /* ==================== Listbox agrupado (§5.1) ==================== */
        <ul
          id={listId}
          role="listbox"
          className="absolute z-10 w-full mt-1 rounded-md border border-hairline bg-paper shadow-overlay max-h-72 overflow-auto"
        >
          {grupos.map(({ categoria, matches: delGrupo }) => (
            <React.Fragment key={categoria.id}>
              <li
                role="presentation"
                className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-sage bg-forest/5"
              >
                {categoria.nombre}
              </li>
              {delGrupo.map(m => renderOpcion(m))}
            </React.Fragment>
          ))}
          {sugerenciasFuzzy.length > 0 && (
            <li
              role="presentation"
              className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-sage bg-forest/5 border-t border-hairline/40"
            >
              Sugerencias
            </li>
          )}
          {sugerenciasFuzzy.map(m => renderOpcion(m))}
          {textoBusqueda ? (
            <li
              role="option"
              aria-selected={false}
              data-ar-guide="concepto-picker-crear"
              className="px-4 py-2 cursor-pointer text-ink border-t border-hairline/40 hover:bg-forest/10"
              onClick={abrirCrear}
            >
              <span className="font-medium">+ Crear</span> “{textoBusqueda}”
            </li>
          ) : null}
          {sinResultados && !textoBusqueda ? (
            <li className="px-4 py-3 text-sm text-sage">
              Sin conceptos{tiposPermitidos ? ' para este tipo de formulario' : ''}
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
};
